import { Drawer } from "@base-ui/react/drawer";
import closeIcon from "@loot-os/icons/icons/close";
import type { ComponentProps, ReactElement, ReactNode } from "react";

import { cn, tv } from "../foundations/cn.js";
import { useMediaQuery } from "../foundations/useMediaQuery.js";
import { Icon } from "./Icon.js";
import { iconButtonStyles } from "./IconButton.styles.js";

type Side = "bottom" | "right" | "left";

const viewportStyles = tv({
  base: "fixed inset-0 z-50 flex",
  variants: {
    side: { bottom: "items-end justify-center", right: "justify-end", left: "justify-start" },
  },
});

const popupStyles = tv({
  base: [
    "flex flex-col overflow-hidden border-border-default bg-bg-surface shadow-float outline-hidden",
    "transition-transform duration-base ease-enter data-ending-style:ease-exit data-swiping:duration-0 data-swiping:select-none motion-reduce:transition-none",
  ],
  variants: {
    side: {
      bottom: [
        "max-h-[calc(100dvh-48px)] w-full rounded-t-3xl border-x border-t pb-[env(safe-area-inset-bottom)] sm:max-w-[640px]",
        "[transform:translateY(var(--drawer-swipe-movement-y))] data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)]",
      ],
      right: [
        "h-full w-[min(var(--sheet-width),calc(100vw-32px))] border-l",
        "[transform:translateX(var(--drawer-swipe-movement-x))] data-starting-style:[transform:translateX(100%)] data-ending-style:[transform:translateX(100%)]",
      ],
      left: [
        "h-full w-[min(var(--sheet-width),calc(100vw-32px))] border-r",
        "[transform:translateX(var(--drawer-swipe-movement-x))] data-starting-style:[transform:translateX(-100%)] data-ending-style:[transform:translateX(-100%)]",
      ],
    },
    size: {
      sm: "[--sheet-width:320px]",
      md: "[--sheet-width:400px]",
      lg: "[--sheet-width:520px]",
    },
  },
  defaultVariants: { size: "md" },
});

const SWIPE: Record<Side, "down" | "right" | "left"> = {
  bottom: "down",
  right: "right",
  left: "left",
};

export interface SheetProps {
  /** The heading; it names the sheet. */
  title: string;
  /** One sentence under the title; it describes the sheet. */
  description?: string;
  /** Hides the heading visually where the content says it already; it still names the sheet. */
  hideTitle?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** An element that opens the sheet, e.g. a Button. */
  trigger?: ReactElement;
  children?: ReactNode;
  /** A SheetFooter with the actions, pinned under the scrolling body. */
  footer?: ReactNode;
  /**
   * The edge it slides from, and the way it swipes closed. `auto`: from the bottom on phones and
   * from the right from md, the shape of a filter or cart panel.
   */
  side?: Side | "auto";
  /** The width of a side sheet. */
  size?: "sm" | "md" | "lg";
  /**
   * `false` leaves the page usable beside it (a panel the shopper works next to): no backdrop,
   * no scroll lock, and focus may leave it.
   */
  modal?: boolean;
  closeLabel?: string;
  /**
   * The content draws its own header, close button and padding (a panel that is the whole
   * screen on a phone). The title still names the sheet, read by screen readers only.
   */
  bare?: boolean;
  className?: string;
}

/**
 * A panel that slides in from an edge: filters, a cart, a menu, a long form on phones. Swipe it
 * toward its edge, press Escape or press outside to close it; focus moves in and returns to the
 * trigger. A bottom sheet keeps a focused field above a phone's on-screen keyboard. For one
 * decision over the page, Modal.
 */
export function Sheet({
  title,
  description,
  hideTitle = false,
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  children,
  footer,
  side: sideProp = "auto",
  size,
  modal = true,
  closeLabel = "Close",
  bare = false,
  className,
}: SheetProps) {
  const desktop = useMediaQuery("(min-width: 768px)");
  const side: Side = sideProp === "auto" ? (desktop ? "right" : "bottom") : sideProp;
  const portal = (
    <Drawer.Portal>
      {modal && (
        <Drawer.Backdrop className="fixed inset-0 z-50 bg-bg-canvas/60 opacity-[calc(1-var(--drawer-swipe-progress,0))] transition-opacity duration-base data-swiping:duration-0 data-ending-style:opacity-0 data-starting-style:opacity-0 motion-reduce:transition-none" />
      )}
      <Drawer.Viewport className={cn(viewportStyles({ side }), !modal && "pointer-events-none")}>
        <Drawer.Popup
          className={cn(popupStyles({ side, size }), !modal && "pointer-events-auto", className)}
        >
          {bare ? (
            <>
              <Drawer.Title className="sr-only">{title}</Drawer.Title>
              {description && (
                <Drawer.Description className="sr-only">{description}</Drawer.Description>
              )}
              <Drawer.Content className="flex min-h-0 flex-1 flex-col">{children}</Drawer.Content>
            </>
          ) : (
            <>
              {side === "bottom" && (
                <div
                  aria-hidden
                  className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-pill bg-border-strong"
                />
              )}
              <div className="flex items-start justify-between gap-4 px-6 pt-5">
                <div className={cn("flex flex-col gap-1", hideTitle && "sr-only")}>
                  <Drawer.Title className="font-body text-heading-h1 text-text-primary">
                    {title}
                  </Drawer.Title>
                  {description && (
                    <Drawer.Description className="font-body text-body-b1 text-text-secondary">
                      {description}
                    </Drawer.Description>
                  )}
                </div>
                <Drawer.Close
                  aria-label={closeLabel}
                  className={cn(
                    iconButtonStyles({ variant: "ghost", size: "sm" }),
                    "-mt-1 -mr-2 ml-auto",
                  )}
                >
                  <Icon icon={closeIcon} />
                </Drawer.Close>
              </div>
              <Drawer.Content className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-6 pt-4 pb-6">
                {children}
              </Drawer.Content>
            </>
          )}
          {footer}
        </Drawer.Popup>
      </Drawer.Viewport>
    </Drawer.Portal>
  );
  return (
    <Drawer.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={(next) => onOpenChange?.(next)}
      swipeDirection={SWIPE[side]}
      modal={modal}
    >
      {trigger && <Drawer.Trigger render={trigger} />}
      {side === "bottom" ? (
        <Drawer.VirtualKeyboardProvider>{portal}</Drawer.VirtualKeyboardProvider>
      ) : (
        portal
      )}
    </Drawer.Root>
  );
}

/** The sheet's actions, pinned under the body: the main one last. */
export function SheetFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-3 border-t border-border-subtle px-6 py-4 sm:flex-row sm:justify-end",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Closes the sheet it sits in; render a Button through it: `<SheetClose render={<Button />}>`. */
export function SheetClose(props: ComponentProps<typeof Drawer.Close>) {
  return <Drawer.Close {...props} />;
}
